import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Text,
  Select,
  Spinner,
  Td,
  Image,
  Button,
  useToast,
  Input,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Badge,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Circle,
  Icon,
  useBreakpointValue,
  Avatar,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import {
  FaTrash,
  FaEllipsisV,
  FaPhone,
  FaEye,
  FaWhatsapp,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { StudentContext } from "../../Context/StudentContext";
import { useLocation } from "../../Context/LocationProvider";
import axios from "axios";
import EditStudentModal from "./EditStudentModal";
import "./student.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../Globalapi/Globalapi";

const StudentsTable = () => {
  const { students, loading, refreshStudents } = useContext(StudentContext);
  const { genders } = useLocation();
  const [visibleStudents, setVisibleStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name"); // Field to search by, default is 'name'
  const [sortOption, setSortOption] = useState(""); // Sorting option
  const [isAscending, setIsAscending] = useState(true); // True if ascending
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [classColors, setClassColors] = useState({});

  const toast = useToast();
  const navigate = useNavigate();
  const INITIAL_LOAD = 600;
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (students.length > 0) {
      setVisibleStudents(students.slice(0, INITIAL_LOAD));
    }
  }, [students]);

  const getGenderName = (genderId) => {
    const gender = genders.find((g) => g.id === genderId);
    return gender ? gender.gender : "Unknown";
  };

  const deleteStudent = async (user_id) => {
    try {
      const token = sessionStorage.getItem("jwtToken");
      const response = await axios.post(
        `${BASE_URL}/admin/student/delete_student.php`,
        { user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setVisibleStudents((prevStudents) =>
          prevStudents.filter((student) => student.user_id !== user_id)
        );
        toast({
          title: "Student deleted.",
          description: "The student has been successfully deleted.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to delete.",
          description: response.data.status || "An error occurred.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while deleting the student.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const headers = [
    "Adm. ",
    "Name",
    "Class",
    "Division",
    "DoB",
    "Email",
    "Gender",
    "Contact",
    "Father",
    "Mother",
    "Status",
    "Action",
  ];
  const handleSortChange = (sortField) => {
    setSortOption(sortField);
    setIsAscending(!isAscending); // Toggle sort direction

    const sortedStudents = [...visibleStudents].sort((a, b) => {
      const aField =
        sortField === "gender"
          ? getGenderName(a.gender_id)
          : sortField === "father_name"
          ? a.father_name
          : sortField === "mother_name"
          ? a.mother_name
          : sortField === "name"
          ? a.name
          : sortField === "admission_number"
          ? a.admission_number
          : "";

      const bField =
        sortField === "gender"
          ? getGenderName(b.gender_id)
          : sortField === "father_name"
          ? b.father_name
          : sortField === "mother_name"
          ? b.mother_name
          : sortField === "name"
          ? b.name
          : sortField === "admission_number"
          ? b.admission_number
          : "";

      if (aField < bField) return isAscending ? -1 : 1;
      if (aField > bField) return isAscending ? 1 : -1;
      return 0;
    });

    setVisibleStudents(sortedStudents);
  };

  // Filter data based on search input and selected search field
  const filteredStudents = visibleStudents.filter((student) => {
    const field =
      searchField === "admission_number"
        ? student.admission_number
        : searchField === "father_name"
        ? student.father_name
        : searchField === "mother_name"
        ? student.mother_name
        : searchField === "contact"
        ? student.father_number // Searching by father's contact number
        : student.name; // Default to name when searchField is "name"

    return field.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const uniqueClassDivisions = [
      ...new Set(students.map((s) => `${s.class}-${s.division}`)),
    ];
    const colors = {};

    uniqueClassDivisions.forEach((classDivision, index) => {
      // First color should be blue (hue around 210°), and then we proceed with the existing color logic
      const hue = index === 0 ? 210 : (index * 137) % 360; // 210° for blue, then your pattern for others
      colors[classDivision] = `hsl(${hue}, 60%, 90%)`; // Lighter pastel shades
    });

    setClassColors(colors);
  }, [students]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredStudents.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderRow = (student) => {
    const rowBgColor = classColors[student.class] || "transparent"; // Apply color based on class

    return (
      <Tr
        key={student.user_id}
        bg={
          classColors[`${student.class}-${student.division}`] || "transparent"
        }
      >
        <Td py="12px" px="20px" textAlign="left">
          <Checkbox />
        </Td>
        <Td py="12px" px="20px" style={{ color: "#3D5EE1" }} textAlign="left">
          {student.admission_number}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          <Link
            to={`/students/detail/${student.user_id}`}
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <Avatar
              src={student.image_url}
              name={student.name}
              alt={student.name}
              size="sm"
              objectFit="cover"
              onError={(e) => {
                e.target.src =
                  "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg";
              }}
            />

            <Text>{student.name}</Text>
          </Link>
        </Td>
        <Td
          py="12px"
          px="20px"
          textAlign="left"
          bg={classColors[student.class] || "transparent"}
        >
          {student.class ? student.class : "No class assigned"}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.division ? student.division : "No class assigned"}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.date_of_birth}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.email}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {getGenderName(student.gender_id)}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.father_number}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.father_name}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          {student.mother_name}
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          <Badge
            colorScheme={student.status === "Active" ? "green" : "green"}
            borderRadius="full"
            px={4}
            py={1}
            fontSize="11px"
            boxShadow="md"
            position="relative"
          >
            {student.status === "Active" ? "Active" : "Active"}
          </Badge>
        </Td>
        <Td py="12px" px="20px" textAlign="left">
          <Flex alignItems="center" gap={2}>
            {/* Phone icon to initiate a call */}
            <Circle
              size="25px"
              bg="white"
              border="1px solid #E9EDF4"
              onClick={() =>
                window.open(`tel:${student.father_number}`, "_self")
              }
              cursor="pointer"
            >
              <Icon as={FaPhone} boxSize={3} color="#515B73" />
            </Circle>

            {/* WhatsApp icon to open WhatsApp */}
            <Circle
              size="25px"
              bg="white"
              border="1px solid #E9EDF4"
              onClick={() =>
                window.open(`https://wa.me/${student.father_number}`, "_blank")
              }
              cursor="pointer"
            >
              <Icon as={FaWhatsapp} boxSize={3} color="#25D366" />
            </Circle>

            {/* Ellipsis icon for the menu options */}
            <Menu>
              <MenuButton as={IconButton} icon={<FaEllipsisV />} size="sm" />
              <MenuList>
                <MenuItem
                  onClick={() =>
                    navigate(`/students/detail/${student.user_id}`)
                  }
                  icon={<FaEye color="black" />}
                >
                  View Student
                </MenuItem>
                <MenuItem>
                  <EditStudentModal
                    studentId={student.user_id}
                    onStudentUpdated={refreshStudents}
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => deleteStudent(student.user_id)}
                  icon={<FaTrash color="red" />}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Td>
      </Tr>
    );
  };
  return (
    <Box p={6} maxWidth="100%">
      {/* Rows Per Page and Search By */}
      <Flex
        direction={isMobile ? "column" : "row"}
        justify={isMobile ? "start" : "space-between"}
        align="center"
        className="sb"
        p={3}
      >
        {/* Rows Per Page Section */}
        <Box mb={isMobile ? 4 : 0}>
          <Flex align="center">
            <Text mr={2}>Rows Per Page</Text>
            <Select
              width="100px"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              borderColor="#E9EDF4"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Select>
          </Flex>
        </Box>

        {/* Filter and Search Section */}
        <Box>
          <Flex align="center" direction={isMobile ? "column" : "row"}>
            <Select
              width="150px"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              borderColor="#E9EDF4"
              w={{ base: "100%" }}
            >
              <option value="name">Name</option> {/* Changed to lowercase */}
              <option value="admission_number">Admission No</option>{" "}
              {/* Changed to lowercase */}
              <option value="father_name">Father Name</option>{" "}
              {/* Changed to lowercase */}
              <option value="mother_name">Mother Name</option>{" "}
              <option value="contact">Contact</option>{" "}
              {/* Added contact option */}
              {/* Changed to lowercase */}
            </Select>
            <Input
              mt={isMobile ? 2 : 0}
              ml={isMobile ? 0 : 4}
              type="text"
              placeholder={`Search by ${
                searchField.charAt(0).toUpperCase() +
                searchField.slice(1).replace("_", " ")
              }`} // Format placeholder
              value={searchTerm}
              borderColor="#E9EDF4"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Flex>
        </Box>
      </Flex>

      <div style={{ overflowX: "auto" }}>
        {loading && currentData.length === 0 ? (
          <Spinner size="xl" />
        ) : currentData.length > 0 ? (
          <Table
            sx={{
              "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              td: {
                whiteSpace: "nowrap", // Prevent text from wrapping
              },
            }}
            overflowX="auto"
          >
            <Thead>
              <Tr>
                <Th>
                  <Checkbox />
                </Th>
                {headers.map((header) => (
                  <Th key={header}>
                    <Flex align="center" justify="space-between">
                      <Text>{header}</Text>
                      {header === "Adm. No" && (
                        <Flex direction="column" align="center" spacing="0.5">
                          <IconButton
                            size="xxs" // Make the buttons extra small
                            icon={<TriangleUpIcon boxSize={2} />} // Reduce the icon size
                            aria-label="Sort Ascending"
                            onClick={() => handleSortChange("admission_number")}
                            mb="1" // Add a little spacing between the icons
                          />
                        </Flex>
                      )}

                      {header === "Name" && (
                        <Flex direction="column" align="center" spacing="0.5">
                          <IconButton
                            size="xxs"
                            icon={<TriangleUpIcon boxSize={2} />}
                            aria-label="Sort Ascending"
                            onClick={() => handleSortChange("name")}
                            mb="1" // Add a little spacing between the icons
                          />
                        </Flex>
                      )}
                      {header === "Gender" && (
                        <Flex direction="column" align="center" spacing="0.5">
                          <IconButton
                            ml={2}
                            size="xxs"
                            icon={<TriangleUpIcon boxSize={2} />}
                            aria-label="Sort Ascending"
                            onClick={() => handleSortChange("gender")}
                            mb="1" // Add a little spacing between the icons
                          />
                        </Flex>
                      )}
                      {header === "Father" && (
                        <Flex direction="column" align="center" spacing="0.5">
                          <IconButton
                            size="xxs"
                            icon={<TriangleUpIcon boxSize={2} />}
                            aria-label="Sort Ascending"
                            onClick={() => handleSortChange("father_name")}
                            mb="1" // Add a little spacing between the icons
                          />
                        </Flex>
                      )}
                      {header === "Mother" && (
                        <Flex direction="column" align="center" spacing="0.5">
                          <IconButton
                            size="xxs"
                            icon={<TriangleUpIcon boxSize={2} />}
                            aria-label="Sort Ascending"
                            onClick={() => handleSortChange("mother_name")}
                            mb="1" // Add a little spacing between the icons
                          />
                        </Flex>
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>{currentData.map((student) => renderRow(student))}</Tbody>
          </Table>
        ) : (
          <Box>
            <Text>No students found.</Text>
          </Box>
        )}
      </div>

      {/* Pagination Controls */}
      <Flex justify="end" borderTopWidth="1px" className="sb2" p={3}>
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>
        <Stack direction="row" mx={2}>
          {currentPage > 1 && (
            <>
              <Button onClick={() => handlePageChange(1)}>1</Button>
              {currentPage > 2 && <Text>...</Text>}
            </>
          )}
          <Button backgroundColor="teal" color="white">
            {currentPage}
          </Button>
          {currentPage < totalPages - 1 && (
            <>
              <Text>...</Text>
              <Button onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </Button>
            </>
          )}
        </Stack>
        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>
    </Box>
  );
};

export default StudentsTable;
